import React, { useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Badge,
  ProgressBar,
  Button,
  Table,
  Spinner,
  Alert,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getReviwQueueDeshboard } from "../../../../../../../redux/slices/tdsSlice";
import { getReviwQueueDeshbordPath } from "../../../../../../../services/config";

const DeductionsReview = () => {
  const dispatch = useDispatch();

  const { reviewQueueData, reviewQueueLoading, reviewQueueError } = useSelector(
    (state) => state.tds
  );

  const storedUser = JSON.parse(localStorage.getItem("tdsUser") || "{}");
  const staffId = storedUser.staffId || storedUser.staff_id || storedUser.id;

  useEffect(() => {
    if (staffId) {
      dispatch(
        getReviwQueueDeshboard({
          path: getReviwQueueDeshbordPath,
          data: {
            userId: storedUser.user_id || storedUser.userId || "",
            staffId: Number(staffId),
            financialYear: storedUser.financialYear || "2025-2026",
          },
        })
      );
    }
  }, [dispatch, staffId]);

  // Loading State
  if (reviewQueueLoading) {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center"
        style={{ minHeight: "100vh", backgroundColor: "#0a0a1a" }}
      >
        <Spinner animation="border" variant="primary" className="mb-4" />
        <h5 className="text-white">Loading Tax Deductions Review...</h5>
        <p className="text-muted">Please wait while we fetch your data</p>
      </div>
    );
  }

  // Error State
  if (reviewQueueError) {
    return (
      <div
        className="text-center py-5"
        style={{ minHeight: "100vh", backgroundColor: "#0a0a1a" }}
      >
        <h4 className="text-danger mb-3">Failed to Load Data</h4>
        <p className="text-muted mb-4">
          {reviewQueueError?.message || "Something went wrong while fetching TDS data."}
        </p>
        <Button variant="outline-light" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }

  // Extract data safely
  const apiData = reviewQueueData?.data || reviewQueueData || {};
  const employeeInfo = apiData.employeeInfo || {};
  const sections = apiData.sections?.deductions?.categories || [];
  const allDocuments = sections.flatMap((cat) => cat.documents || []);
  const overallStatus = apiData.overallStatus || {};

  const trustScore = overallStatus?.completionPercentage || 0;

  // Check if payroll/TDS is not configured or no declarations yet
  const isPayrollNotConfigured = sections.length === 0 || !apiData.sections?.deductions;

  if (isPayrollNotConfigured) {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center text-center"
        style={{ minHeight: "100vh", backgroundColor: "#0a0a1a", padding: "2rem" }}
      >
        <div className="mb-5">
          <i className="display-1 text-muted">📅</i>
        </div>
        <h3 className="text-white mb-3">Tax Declarations Not Yet Configured</h3>
        <p className="text-muted lead mb-4" style={{ maxWidth: "600px" }}>
          Payroll and TDS settings for this employee have not been configured yet,
          or no tax declarations have been submitted for the current financial year.
        </p>
        <p className="text-info mb-5">
          Please configure payroll settings or wait for the employee to submit their
          investment declarations. This feature will be available shortly.
        </p>
        <Alert variant="info" className="mx-auto" style={{ maxWidth: "500px" }}>
          <strong>Give us some time!</strong> We're setting things up. You'll be able
          to review deductions once declarations are submitted.
        </Alert>
        <Button variant="outline-primary" size="lg" className="mt-4">
          Refresh Page
        </Button>
      </div>
    );
  }

  // Normal rendering when data exists
  const sectionConfig = {
    "80C": { color: "purple", icon: "Money Bag", title: "Section 80C", desc: "Investment & Savings Limit ₹1,50,000" },
    "80CCD": { color: "indigo", icon: "Bank", title: "Section 80CCD", desc: "National Pension Scheme" },
    "80D": { color: "green", icon: "Hospital", title: "Section 80D", desc: "Medical Insurance Premiums" },
    "80E": { color: "orange", icon: "Book", title: "Section 80E", desc: "Education Loan Interest (No Limit)" },
    "80G": { color: "pink", icon: "Heart", title: "Section 80G", desc: "Donations to Charitable Institutions" },
  };

  const getColorClass = (color) => {
    const map = {
      purple: "bg-purple text-white",
      indigo: "bg-primary text-white",
      green: "bg-success text-white",
      orange: "bg-warning text-dark",
      pink: "bg-danger text-white",
    };
    return map[color] || "bg-secondary text-white";
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
      case "verified":
        return <Badge bg="success" pill>Verified</Badge>;
      case "partial":
        return <Badge bg="warning" text="dark" pill>Partial</Badge>;
      case "rejected":
        return <Badge bg="danger" pill>Rejected</Badge>;
      default:
        return <Badge bg="secondary" pill>Pending</Badge>;
    }
  };

  return (
    <div style={{ backgroundColor: "#0a0a1a", minHeight: "100vh", color: "#e0e0e0" }}>
      <div className="container-fluid py-4 px-3 px-md-5">
        <Row className="g-4">
          {/* Left Column - Deductions */}
          <Col lg={8}>
            <h4 className="text-white mb-4">Tax Deductions Review</h4>

            {sections.map((section) => {
              const config = sectionConfig[section.section] || {
                color: "secondary",
                icon: "Document",
                title: `Section ${section.section}`,
                desc: "",
              };

              return (
                <Card
                  key={section.section}
                  className="mb-4 border-0 shadow-sm"
                  style={{ backgroundColor: "#16213e" }}
                >
                  <Card.Body className="p-4">
                    <Row className="align-items-center">
                      <Col md={8}>
                        <div className="d-flex align-items-center gap-4">
                          <div
                            className={`p-3 rounded-3 fs-4 ${getColorClass(config.color)}`}
                          >
                            {config.icon}
                          </div>
                          <div>
                            <h5 className="text-white mb-1">{config.title}</h5>
                            <p className="text-muted small mb-0">{config.desc}</p>
                          </div>
                        </div>
                      </Col>
                      <Col md={4} className="text-md-end">
                        {getStatusBadge(section.status)}
                        <small className="d-block text-muted mt-1">
                          {section.documents?.length || 0} docs
                        </small>
                      </Col>
                    </Row>

                    <Row className="mt-4">
                      <Col>
                        <div className="d-flex justify-content-between">
                          <div>
                            <span className="text-muted">Claimed</span>
                            <h4 className="text-white mb-0">
                              ₹{(section.amount || 0).toLocaleString("en-IN")}
                            </h4>
                          </div>
                          <div className="text-end">
                            <span className="text-muted">Allowed</span>
                            <h4 className="text-success mb-0">
                              ₹{(section.allowedAmount || section.amount || 0).toLocaleString("en-IN")}
                            </h4>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="mt-4 d-flex gap-3 flex-wrap">
                      <Button variant="outline-secondary" size="sm">
                        Recalculate
                      </Button>
                      <Button variant="success" size="sm">
                        Approve
                      </Button>
                      <Button variant="danger" size="sm">
                        Reject
                      </Button>
                      <Button variant="outline-light" size="sm">
                        Comment
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              );
            })}

            <div className="text-center mt-5">
              <Button variant="success" size="lg" className="mx-2 px-5">
                Approve All
              </Button>
              <Button variant="danger" size="lg" className="mx-2 px-5">
                Reject
              </Button>
              <Button variant="outline-light" size="lg" className="mx-2 px-5">
                Comment
              </Button>
            </div>
          </Col>

          {/* Right Column */}
          <Col lg={4}>
            {/* Proof Analysis */}
            <Card className="mb-4 border-0" style={{ backgroundColor: "#16213e" }}>
              <Card.Header className="border-0 text-white" style={{ backgroundColor: "#1e1e3f" }}>
                <h6 className="mb-0">Proof Analysis</h6>
              </Card.Header>
              <Card.Body className="text-center">
                <h2 className="text-success display-5 fw-bold">{trustScore}%</h2>
                <ProgressBar
                  now={trustScore}
                  variant="success"
                  className="my-3"
                  style={{ height: "12px" }}
                />
                <p className="text-muted small">
                  {trustScore >= 90
                    ? "High confidence • All critical fields verified"
                    : "Review recommended"}
                </p>
                <p className="text-muted small">{allDocuments.length} documents processed</p>
              </Card.Body>
            </Card>

            {/* OCR Verification Summary */}
            <Card className="mb-4 border-0" style={{ backgroundColor: "#16213e" }}>
              <Card.Header className="border-0 text-white" style={{ backgroundColor: "#1e1e3f" }}>
                <h6 className="mb-0">OCR Verification Summary</h6>
              </Card.Header>
              <Card.Body className="p-0">
                <Table variant="dark" className="mb-0">
                  <thead>
                    <tr className="text-muted small">
                      <th>Field Type</th>
                      <th className="text-center">Matches</th>
                      <th className="text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Employee Name</td>
                      <td className="text-center">{employeeInfo.employeeName ? "8.0" : "0.0"}</td>
                      <td className="text-center text-success">{employeeInfo.employeeName ? "Verified" : "Warning"}</td>
                    </tr>
                    <tr>
                      <td>PAN Number</td>
                      <td className="text-center">{employeeInfo.employeePAN ? "8.5" : "0.0"}</td>
                      <td className="text-center text-success">{employeeInfo.employeePAN ? "Verified" : "Warning"}</td>
                    </tr>
                    <tr>
                      <td>Amount Values</td>
                      <td className="text-center">7.8</td>
                      <td className="text-center text-warning">Warning</td>
                    </tr>
                    <tr>
                      <td>Financial Year</td>
                      <td className="text-center">8.0</td>
                      <td className="text-center text-success">Verified</td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>

            {/* Document Gallery */}
            <Card className="border-0" style={{ backgroundColor: "#16213e" }}>
              <Card.Header className="border-0 text-white d-flex justify-content-between" style={{ backgroundColor: "#1e1e3f" }}>
                <h6 className="mb-0">Document Gallery</h6>
              </Card.Header>
              <Card.Body>
                {allDocuments.length > 0 ? (
                  allDocuments.slice(0, 5).map((doc, i) => (
                    <div key={i} className="d-flex align-items-center gap-3 py-3 border-bottom border-secondary">
                      <div className="bg-secondary rounded p-3">
                        Document
                      </div>
                      <div className="flex-grow-1">
                        <p className="mb-0 text-white small">{doc.name || `Document ${i + 1}`}</p>
                        <p className="text-muted mb-0" style={{ fontSize: "0.75rem" }}>
                          {doc.section || "Unknown"} • {doc.size || "PDF"}
                        </p>
                      </div>
                      <Badge bg="success" pill>Verified</Badge>
                    </div>
                  ))
                ) : (
                  <p className="text-muted text-center py-4">No documents uploaded yet</p>
                )}
              </Card.Body>
              <Card.Footer className="border-0 bg-transparent">
                <Button variant="primary" className="w-100" disabled={allDocuments.length === 0}>
                  View All Documents
                </Button>
              </Card.Footer>
            </Card>

            {/* Actions */}
            <div className="mt-4 d-grid gap-3">
              <Button variant="primary" size="lg">
                Export
              </Button>
              <Button variant="secondary" size="lg">
                Print
              </Button>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default DeductionsReview;
